import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { FormProvider, Path, useForm } from "react-hook-form";

import { useCompany } from "@/common/providers/company-provider";
import { combineCallingCodeWithCountry } from "@/common/utils/phone";
import { CompanyDetailToForm, CompanyTo } from "@/generated/api/users";

import { InputField } from "../form/input-field/input-field";
import { PhoneNumber } from "../form/phone-number/phone-number";
import { useCountryCode } from "../user-settings/hooks/use-country-code";

type Fields = {
  companyName: string;
  companyNumber: string;
  vat: string;
  legalForm: CompanyDetailToForm;
  address: string;
  contact: {
    name: string;
    role: string;
    email: string;
    phone: {
      countryCode: string;
      phoneNumber: string;
    };
  };
};

const CompanyForm = ({
  initialValues,
  disabledFields,
  omitFields = [],
  onSubmit,
}: {
  initialValues: Fields;
  disabledFields: Array<Path<Fields>>;
  omitFields?: Array<Path<Fields>>;
  onSubmit: (data: Fields, e?: React.BaseSyntheticEvent) => void;
}) => {
  const {
    user: { language },
  } = useCompany();
  const { phoneCountryCodes } = useCountryCode(language);
  const form = useForm<Fields>({
    defaultValues: initialValues,
  });

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-y-6"
        id="company-form"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <InputField<Fields>
          disabled
          label={<Trans>Název společnosti</Trans>}
          name="companyName"
        />

        <InputField<Fields>
          disabled
          label={<Trans>IČO společnosti</Trans>}
          name="companyNumber"
        />

        <InputField<Fields>
          disabled={disabledFields.includes("vat")}
          label={<Trans>DIČ</Trans>}
          name="vat"
        />

        <InputField<Fields>
          disabled={disabledFields.includes("address")}
          label={<Trans>Adresa</Trans>}
          name="address"
          options={{
            required: t`Vyplňte prosím adresu`,
          }}
        />

        {omitFields.includes("legalForm") ? null : (
          <label className="group" htmlFor="legalForm">
            <Trans>Forma</Trans>
            <select
              className="group-has-[[role=alert]]:border-can-russet mt-2 block h-[calc(--spacing(14)+2px)] w-full rounded-lg font-bold"
              disabled={disabledFields.includes("legalForm")}
            >
              <option value={CompanyDetailToForm.JURIDICAL_PERSON}>
                <Trans>Právnická osoba</Trans>
              </option>
              <option value={CompanyDetailToForm.NATURAL_PERSON}>
                <Trans>Fyzická osoba</Trans>
              </option>
            </select>
          </label>
        )}

        <h2 className="text-can-forest-teal font-bold">
          <Trans>Kontaktní informace</Trans>
        </h2>

        {omitFields.includes("contact.name") ? null : (
          <InputField<Fields>
            label={<Trans>Kontaktní osoba</Trans>}
            name="contact.name"
            options={{
              required: t`Vyplňte prosím jméno kontaktní osoby`,
            }}
          />
        )}

        {omitFields.includes("contact.role") ? null : (
          <InputField<Fields>
            label={<Trans context="job role">Pozice</Trans>}
            name="contact.role"
            options={{
              required: t`Vyplňte prosím pozici`,
            }}
          />
        )}

        <InputField<Fields>
          label={<Trans>E-mailová adresa</Trans>}
          name="contact.email"
          options={{
            required: t`Vyplňte prosím e-mail`,
          }}
          type="email"
        />

        <PhoneNumber
          callingCodes={phoneCountryCodes.data}
          name="contact.phone"
        />
      </form>
    </FormProvider>
  );
};

const getInitialValues = (
  company: Omit<CompanyTo, "planExpiresAt" | "subscriptions">,
) => ({
  address: company.detail?.address ?? "",
  companyName: company.name,
  companyNumber: company.companyNumber,
  contact: {
    email: company.detail?.contactEmail ?? "",
    name: company.detail?.contactName ?? "",
    phone: {
      countryCode: company.detail?.contactPhone
        ? combineCallingCodeWithCountry({
            id: company.country,
            phoneCountryCode: company.detail.contactPhone.countryCode,
          })
        : company.country,
      phoneNumber: company.detail?.contactPhone?.phoneNumber ?? "",
    },
    role: company.detail?.contactPosition ?? "",
  },
  legalForm: company.detail?.form ?? CompanyDetailToForm.JURIDICAL_PERSON,
  vat: company.detail?.vat ?? "",
});

export { CompanyForm, getInitialValues };
export type { Fields };
